import { create } from 'zustand';
import { api } from '../services/apiService';
import type { FileSection, FileContent, MemoryHealth } from '../types/memory';

interface MemoryState {
  sections: FileSection[];
  selectedPath: string | null;
  fileContent: FileContent | null;
  health: MemoryHealth | null;
  loading: boolean;
  contentLoading: boolean;
  searchQuery: string;
  fetchFiles: () => Promise<void>;
  fetchFile: (path: string) => Promise<void>;
  fetchHealth: () => Promise<void>;
  setSelectedPath: (path: string) => void;
  setSearchQuery: (query: string) => void;
}

export const useMemoryStore = create<MemoryState>((set, get) => ({
  sections: [],
  selectedPath: null,
  fileContent: null,
  health: null,
  loading: false,
  contentLoading: false,
  searchQuery: '',

  fetchFiles: async () => {
    set({ loading: true });
    try {
      const sections = await api.get<FileSection[]>('/memory/files');
      set({ sections });
    } catch (err) {
      console.error('Failed to fetch memory files:', err);
    } finally {
      set({ loading: false });
    }
  },

  fetchFile: async (path: string) => {
    set({ contentLoading: true });
    try {
      const fileContent = await api.get<FileContent>(`/memory/file?path=${encodeURIComponent(path)}`);
      set({ fileContent });
    } catch (err) {
      console.error('Failed to fetch file content:', err);
      set({ fileContent: null });
    } finally {
      set({ contentLoading: false });
    }
  },

  fetchHealth: async () => {
    try {
      const health = await api.get<MemoryHealth>('/memory/health');
      set({ health });
    } catch (err) {
      console.error('Failed to fetch memory health:', err);
    }
  },

  setSelectedPath: (path: string) => {
    set({ selectedPath: path });
    get().fetchFile(path);
  },

  setSearchQuery: (query: string) => {
    set({ searchQuery: query });
  },
}));
